import { memo, useEffect, useState, useCallback, useRef } from 'react';
import { 
  Activity, Clock, CheckCircle, XCircle, Loader2, 
  RefreshCw, Trash2, ChevronDown, ChevronRight,
  Image, Video, Music, Zap, Sparkles, Wand2, Film, Pencil, Star,
  X, Timer, ExternalLink, AlertTriangle, Coins
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NODE_ICONS = {
  enhance_photo: Sparkles,
  create_video: Video,
  video_with_sound: Video,
  fabric_avatar: Video,
  dream_actor: Video,
  nano_banana: Image,
  background_removal: Image,
  style_transfer: Wand2,
  upscale: Zap,
  lyria2: Music,
  qwen3_tts: Music,
  frame_extractor: Film,
  lyria3_pro: Music,
  image_edit: Pencil,
  banana2pro: Star,
  imagen4_ultra: Sparkles,
  seedance_25: Video,
  pixverse_v6: Film,
  elevenlabs_v2: Music,
  dubbing: Music,
  lipsync: Video,
  music_pro: Music,
  // Legacy node_type keys for backward compat
  imageEnhancement: Sparkles,
  videoGeneration: Video,
  videoWithSound: Video,
  fabricAvatar: Video,
  dreamActor: Video,
  klingAvatar: Video,
  nanoBanana: Image,
  bgRemoval: Image,
  frameExtractor: Film,
  lyria3pro: Music,
  imageEdit: Pencil,
  banana2pro: Star,
  imagen4Ultra: Sparkles,
  seedance25: Video,
  pixverseV6: Film,
  eleV2: Music,
  elTraductor: Music,
  lipSync: Video,
  musicPro: Music,
  avatarFast: Zap,
  styleTransfer: Wand2,
};

const NODE_NAMES = {
  enhance_photo: 'Mejorar Imagen',
  create_video: 'Crear Video',
  video_with_sound: 'Video con Sonido',
  fabric_avatar: 'Avatar Parlante',
  dream_actor: 'DreamActor',
  kling_avatar: 'Avatar V2 Kling',
  nano_banana: 'Nano Banana',
  background_removal: 'Remover Fondo',
  style_transfer: 'Style Transfer',
  upscale: 'Upscale',
  lyria2: 'Lyria 2',
  qwen3_tts: 'Qwen3 TTS',
  frame_extractor: 'Frame Extractor',
  lyria3_pro: 'Lyria 3 Full Song',
  image_edit: 'Image Edit',
  banana2pro: 'Banana 2 Pro',
  imagen4_ultra: 'Image - 4Ultra',
  seedance_25: 'Video seedance-2.5',
  pixverse_v6: 'Pixverse V6',
  elevenlabs_v2: 'EleV2',
  dubbing: 'El_Traductor',
  lipsync: 'LipSinc',
  music_pro: 'Music Pro',
  // Legacy
  imageEnhancement: 'Mejorar Imagen',
  videoGeneration: 'Crear Video',
  videoWithSound: 'Video con Sonido',
  fabricAvatar: 'Avatar Parlante',
  dreamActor: 'DreamActor',
  klingAvatar: 'Avatar V2 Kling',
  nanoBanana: 'Nano Banana',
  bgRemoval: 'Remover Fondo',
  frameExtractor: 'Frame Extractor',
  lyria3pro: 'Lyria 3 Full Song',
  imageEdit: 'Image Edit',
  banana2pro: 'Banana 2 Pro',
  imagen4Ultra: 'Image - 4Ultra',
  seedance25: 'Video seedance-2.5',
  pixverseV6: 'Pixverse V6',
  eleV2: 'EleV2',
  elTraductor: 'El_Traductor',
  lipSync: 'LipSinc',
  musicPro: 'Music Pro',
  avatarFast: 'Avatar Fast',
  styleTransfer: 'Style Transfer',
};

const NODE_COLORS = {
  enhance_photo: '#f59e0b',
  create_video: '#8b5cf6',
  video_with_sound: '#06b6d4',
  fabric_avatar: '#ec4899',
  dream_actor: '#f97316',
  kling_avatar: '#a855f7',
  nano_banana: '#10b981',
  background_removal: '#6366f1',
  style_transfer: '#a855f7',
  upscale: '#eab308',
  lyria2: '#3b82f6',
  qwen3_tts: '#14b8a6',
  frame_extractor: '#0ea5e9',
  lyria3_pro: '#d946ef',
  image_edit: '#f472b6',
  banana2pro: '#fbbf24',
  imagen4_ultra: '#818cf8',
  seedance_25: '#22d3ee',
  pixverse_v6: '#fb7185',
  elevenlabs_v2: '#4ade80',
  dubbing: '#a3e635',
  lipsync: '#e879f9',
  music_pro: '#2dd4bf',
  // Legacy
  imageEnhancement: '#f59e0b',
  videoGeneration: '#8b5cf6',
  videoWithSound: '#06b6d4',
  fabricAvatar: '#ec4899',
  dreamActor: '#f97316',
  klingAvatar: '#a855f7',
  nanoBanana: '#10b981',
  bgRemoval: '#6366f1',
  frameExtractor: '#0ea5e9',
  lyria3pro: '#d946ef',
  imageEdit: '#f472b6',
  banana2pro: '#fbbf24',
  imagen4Ultra: '#818cf8',
  seedance25: '#22d3ee',
  pixverseV6: '#fb7185',
  eleV2: '#4ade80',
  elTraductor: '#a3e635',
  lipSync: '#e879f9',
  musicPro: '#2dd4bf',
  avatarFast: '#facc15',
  styleTransfer: '#a855f7',
};

const ExecutionHistory = memo(({ isOpen, onClose }) => {
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const panelRef = useRef(null);

  const loadExecutions = useCallback(async () => {
    setLoading(true);
    try {
      const [execRes, statsRes] = await Promise.all([
        axios.get(`${API}/executions?limit=100`),
        axios.get(`${API}/executions/stats/summary`),
      ]);
      const execList = execRes.data.executions || [];
      setExecutions(execList);
      // Map Supabase stats format to expected shape
      const s = statsRes.data.stats || statsRes.data;
      setStats({
        total: s.total || 0,
        completed: s.completed || 0,
        failed: s.failed || 0,
        running: (s.running || 0) + (s.queued || 0) + (s.processing || 0),
        success_rate: s.success_rate || 0,
        average_duration_seconds: s.average_duration_seconds || 0,
        total_credits_used: s.total_credits_used || 0,
        total_duration_seconds: s.total_duration_seconds || 0,
      });
    } catch (error) {
      console.error('Error loading executions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadExecutions();
    }
  }, [isOpen, loadExecutions]);

  // While the panel is open AND there are queued/processing executions,
  // auto-refresh every 4s so the user sees state transitions live.
  useEffect(() => {
    if (!isOpen) return;
    const hasActive = executions.some(
      e => e.status === 'queued' || e.status === 'processing' || e.status === 'running'
    );
    if (!hasActive) return;
    const t = setInterval(loadExecutions, 4000);
    return () => clearInterval(t);
  }, [isOpen, executions, loadExecutions]);

  const handleClearHistory = async () => {
    if (!window.confirm('¿Eliminar todo el historial de ejecuciones?')) return;
    
    try {
      await axios.delete(`${API}/executions/clear`);
      setExecutions([]);
      setStats(null);
      await loadExecutions();
    } catch (error) {
      console.error('Error clearing executions:', error);
    }
  };

  // Group executions by result_type (Supabase) or node_type (legacy)
  const groupedExecutions = executions.reduce((acc, exec) => {
    const nodeType = exec.result_type || exec.node_type || 'unknown';
    if (!acc[nodeType]) {
      acc[nodeType] = {
        executions: [],
        totalTime: 0,
        totalCredits: 0,
        completed: 0,
        failed: 0,
        running: 0,
      };
    }
    acc[nodeType].executions.push(exec);
    acc[nodeType].totalTime += exec.duration_seconds || 0;
    acc[nodeType].totalCredits += exec.credits_used || 0;
    if (exec.status === 'completed') acc[nodeType].completed++;
    else if (exec.status === 'failed') acc[nodeType].failed++;
    else if (exec.status === 'running' || exec.status === 'processing' || exec.status === 'queued') acc[nodeType].running++;
    return acc;
  }, {});

  // Calculate total generation time
  const totalGenerationTime = executions.reduce((sum, exec) => sum + (exec.duration_seconds || 0), 0);

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    if (isToday) return 'Hoy';
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={12} className="exec-status-icon success" />;
      case 'failed': return <XCircle size={12} className="exec-status-icon error" />;
      case 'running': return <Loader2 size={12} className="exec-status-icon running animate-spin" />;
      default: return <Clock size={12} className="exec-status-icon" />;
    }
  };

  const toggleNodeExpand = (nodeType) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeType]: !prev[nodeType]
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const nodeTypes = Object.keys(groupedExecutions);
  const runningExecutions = executions.filter(e => e.status === 'running' || e.status === 'processing' || e.status === 'queued');

  return (
    <div className="exec-history-overlay" onClick={handleOverlayClick}>
      <div className="exec-history-panel" ref={panelRef}>
        {/* Header */}
        <div className="exec-history-header">
          <div className="exec-header-title">
            <Activity size={18} />
            <span>Historial de Ejecuciones</span>
          </div>
          <div className="exec-header-actions">
            <button 
              className="exec-action-btn"
              onClick={loadExecutions}
              disabled={loading}
              title="Refrescar"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button 
              className="exec-action-btn danger"
              onClick={handleClearHistory}
              disabled={executions.length === 0}
              title="Limpiar historial"
            >
              <Trash2 size={14} />
            </button>
            <button className="exec-close-btn" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Quick Stats Bar */}
        {stats && (
          <div className="exec-quick-stats">
            <div className="exec-stat-item">
              <span className="exec-stat-number">{stats.total}</span>
              <span className="exec-stat-label">Total</span>
            </div>
            <div className="exec-stat-divider" />
            <div className="exec-stat-item success">
              <span className="exec-stat-number">{stats.completed}</span>
              <span className="exec-stat-label">Exitosas</span>
            </div>
            <div className="exec-stat-divider" />
            <div className="exec-stat-item error">
              <span className="exec-stat-number">{stats.failed}</span>
              <span className="exec-stat-label">Fallidas</span>
            </div>
            <div className="exec-stat-divider" />
            <div className="exec-stat-item">
              <span className="exec-stat-number">{stats.success_rate}%</span>
              <span className="exec-stat-label">Éxito</span>
            </div>
            <div className="exec-stat-divider" />
            <div className="exec-stat-item credits" data-testid="exec-stat-credits">
              <span className="exec-stat-number">{stats.total_credits_used}</span>
              <span className="exec-stat-label">Créditos</span>
            </div>
            <div className="exec-stat-divider" />
            <div className="exec-stat-item" data-testid="exec-stat-time">
              <span className="exec-stat-number">{formatDuration(stats.total_duration_seconds)}</span>
              <span className="exec-stat-label">Tiempo</span>
            </div>
          </div>
        )}

        {/* Running Executions (if any) */}
        {runningExecutions.length > 0 && (
          <div className="exec-running-section" data-testid="exec-active-section">
            <div className="exec-running-header">
              <Loader2 size={14} className="animate-spin" />
              <span>
                Activas ({runningExecutions.length})
                {(() => {
                  const proc = runningExecutions.filter(e => e.status === 'processing' || e.status === 'running').length;
                  const queued = runningExecutions.filter(e => e.status === 'queued').length;
                  return (
                    <span className="exec-active-breakdown">
                      {proc > 0 && <> · {proc} procesando</>}
                      {queued > 0 && <> · {queued} en cola</>}
                    </span>
                  );
                })()}
              </span>
            </div>
            <div className="exec-running-list">
              {runningExecutions.map((exec, idx) => {
                const typeKey = exec.result_type || exec.node_type;
                const Icon = NODE_ICONS[typeKey] || Activity;
                const color = NODE_COLORS[typeKey] || '#6b7280';
                const isQueued = exec.status === 'queued';
                // Compute queue position among queued items (1-indexed)
                let queuePos = 0;
                if (isQueued) {
                  queuePos = runningExecutions
                    .slice(0, idx + 1)
                    .filter(e => e.status === 'queued').length;
                }
                return (
                  <div
                    key={exec.id}
                    className={`exec-running-item ${isQueued ? 'is-queued' : 'is-processing'}`}
                    data-testid={`exec-active-item-${exec.id}`}
                  >
                    <div className="exec-running-icon" style={{ backgroundColor: `${color}20`, color }}>
                      <Icon size={14} />
                    </div>
                    <div className="exec-running-info">
                      <span className="exec-running-name">
                        {NODE_NAMES[typeKey] || typeKey}
                      </span>
                      <span className="exec-running-time">
                        {isQueued
                          ? <><Clock size={10} /> En cola · posición {queuePos}</>
                          : <>Iniciado: {formatTime(exec.started_at || exec.start_time || exec.created_at)}</>
                        }
                      </span>
                    </div>
                    {isQueued ? (
                      <span className="exec-queue-pos-badge" data-testid={`exec-queue-pos-${exec.id}`}>#{queuePos}</span>
                    ) : (
                      <Loader2 size={16} className="animate-spin exec-running-spinner" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content - Grouped by Node */}
        <div className="exec-history-content">
          {loading ? (
            <div className="exec-loading">
              <Loader2 size={24} className="animate-spin" />
              <span>Cargando historial...</span>
            </div>
          ) : executions.length === 0 ? (
            <div className="exec-empty">
              <Activity size={40} />
              <span>Sin ejecuciones</span>
              <p>El historial de procesamiento aparecerá aquí cuando ejecutes nodos</p>
            </div>
          ) : (
            <div className="exec-nodes-list">
              {nodeTypes.map(nodeType => {
                const group = groupedExecutions[nodeType];
                const Icon = NODE_ICONS[nodeType] || Activity;
                const color = NODE_COLORS[nodeType] || '#6b7280';
                const isExpanded = expandedNodes[nodeType] !== false; // Default expanded
                const completedExecs = group.executions.filter(e => !['running', 'processing', 'queued'].includes(e.status));
                
                return (
                  <div key={nodeType} className="exec-node-group">
                    {/* Node Header */}
                    <div 
                      className="exec-node-header"
                      onClick={() => toggleNodeExpand(nodeType)}
                    >
                      <div className="exec-node-left">
                        <div className="exec-node-icon" style={{ backgroundColor: `${color}20`, color }}>
                          <Icon size={16} />
                        </div>
                        <div className="exec-node-info">
                          <span className="exec-node-name">
                            {NODE_NAMES[nodeType] || nodeType}
                          </span>
                          <span className="exec-node-count">
                            {group.executions.length} ejecución{group.executions.length !== 1 ? 'es' : ''}
                          </span>
                        </div>
                      </div>
                      <div className="exec-node-right">
                        <div className="exec-node-stats">
                          {group.completed > 0 && (
                            <span className="exec-mini-stat success">
                              <CheckCircle size={10} /> {group.completed}
                            </span>
                          )}
                          {group.failed > 0 && (
                            <span className="exec-mini-stat error">
                              <XCircle size={10} /> {group.failed}
                            </span>
                          )}
                        </div>
                        <span className="exec-node-time">
                          <Timer size={12} />
                          {formatDuration(group.totalTime)}
                        </span>
                        {group.totalCredits > 0 && (
                          <span className="exec-node-credits">
                            <Coins size={12} />
                            {group.totalCredits}
                          </span>
                        )}
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </div>
                    </div>

                    {/* Node Executions List */}
                    {isExpanded && (
                      <div className="exec-node-list">
                        {completedExecs.map(exec => (
                          <div 
                            key={exec.id} 
                            className={`exec-item ${exec.status}`}
                          >
                            <div className="exec-item-main">
                              {getStatusIcon(exec.status)}
                              <div className="exec-item-info">
                                <span className="exec-item-date">
                                  {formatDate(exec.started_at || exec.start_time || exec.created_at)} {formatTime(exec.started_at || exec.start_time || exec.created_at)}
                                </span>
                                {exec.input_files && exec.input_files.length > 0 && (
                                  <span className="exec-item-files">
                                    {exec.input_files.join(', ')}
                                  </span>
                                )}
                              </div>
                              <div className="exec-item-meta">
                                {exec.output_type && (
                                  <span className="exec-item-type">{exec.output_type}</span>
                                )}
                                <span className="exec-item-duration">
                                  {formatDuration(exec.duration_seconds)}
                                </span>
                                {(exec.credits_used > 0 || exec.credits_used === 0) && exec.status === 'completed' && (
                                  <span className="exec-item-credits" data-testid={`exec-credits-${exec.id}`}>
                                    {exec.credits_used > 0 ? exec.credits_used : '0'} cr
                                  </span>
                                )}
                                {(exec.output_url || (exec.results && exec.results[0]?.output_url)) && (
                                  <a 
                                    href={exec.output_url || exec.results[0].output_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="exec-item-link"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Ver resultado"
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                )}
                              </div>
                            </div>
                            {exec.error_message && (
                              <div className="exec-item-error">
                                <AlertTriangle size={12} />
                                <span>{exec.error_message}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Total Generation Time Footer */}
        {executions.length > 0 && (
          <div className="exec-total-footer">
            <div className="exec-total-content">
              <Timer size={16} />
              <span className="exec-total-label">Tiempo Total de Generación:</span>
              <span className="exec-total-value">{formatDuration(totalGenerationTime)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ExecutionHistory.displayName = 'ExecutionHistory';

export default ExecutionHistory;
